/* eslint-disable no-console */
import morphdom from "morphdom";

import { CueCard } from "./cue-card";
export * from "./cue-card";

declare global {
  interface Window {
    __kenobiIdentityResolution?: unknown;
  }
}

const VISITOR_QUERY_PARAM_KEY = "kfor";
const VISITOR_SESSION_STORAGE_KEY = "kenobi_visitor_key";
const TRANSFORMATION_DATA_ATTR_KEY = "data-kenobi-transformation-identifier";

type AnimationSplitTarget = "line" | "word" | "character";

export type AnimationName = string;

export type Easing = string;

// Configurable overflow behavior for transition wrapper to prevent text clipping (e.g., gradient text descenders)
export type TransitionOverflow = "hidden" | "visible" | "clip";

export type KeyframeDefinition = Keyframe | Keyframe[];
export interface TextAnimationConfig {
  by: AnimationSplitTarget;
  delayMs?: number;
  staggerMs?: number;
  animationName?: AnimationName;
  keyframes?: KeyframeDefinition;
  durationMs?: number;
}

export interface AnimationSettings {
  animationName?: AnimationName;
  keyframes?: KeyframeDefinition;
  durationMs?: number;
  delayMs?: number;
}

export interface TransitionConfig {
  enabled?: boolean;
  /** The master duration for symmetrical transitions. */
  durationMs?: number;
  /** The master delay for symmetrical transitions. */
  delayMs?: number;
  easing?: Easing;
  animateSize?: boolean;
  /** Controls wrapper overflow to prevent clipping of text with descenders or gradient effects */
  overflow?: TransitionOverflow;
  textAnimation?: TextAnimationConfig;
  enter?: AnimationSettings;
  exit?: AnimationSettings;
}

type NormalizedTransitionConfig = Required<
  Pick<
    TransitionConfig,
    "enabled" | "durationMs" | "delayMs" | "easing" | "animateSize" | "overflow"
  >
> &
  TransitionConfig;

type AnimatableElement = HTMLElement | SVGElement;

const DEFAULT_TRANSITION_CONFIG: NormalizedTransitionConfig = {
  enabled: false,
  durationMs: 300,
  delayMs: 0,
  easing: "ease",
  animateSize: true,
  overflow: "hidden", // Default to hidden for clean size animations; use "visible" for gradient text
};

const createNormalizedTransitionConfig = (
  config?: TransitionConfig
): NormalizedTransitionConfig => ({
  enabled: config?.enabled ?? DEFAULT_TRANSITION_CONFIG.enabled,
  durationMs: config?.durationMs ?? DEFAULT_TRANSITION_CONFIG.durationMs,
  delayMs: config?.delayMs ?? DEFAULT_TRANSITION_CONFIG.delayMs,
  easing: config?.easing ?? DEFAULT_TRANSITION_CONFIG.easing,
  animateSize: config?.animateSize ?? DEFAULT_TRANSITION_CONFIG.animateSize,
  overflow: config?.overflow ?? DEFAULT_TRANSITION_CONFIG.overflow,
  textAnimation: config?.textAnimation,
  enter: config?.enter,
  exit: config?.exit,
});

const mergeTransitionConfig = (
  base: NormalizedTransitionConfig,
  override?: TransitionConfig
): NormalizedTransitionConfig => ({
  enabled: override?.enabled ?? base.enabled,
  durationMs: override?.durationMs ?? base.durationMs,
  delayMs: override?.delayMs ?? base.delayMs,
  easing: override?.easing ?? base.easing,
  animateSize: override?.animateSize ?? base.animateSize,
  overflow: override?.overflow ?? base.overflow,
  textAnimation: override?.textAnimation ?? base.textAnimation,
  enter: override?.enter ?? base.enter,
  exit: override?.exit ?? base.exit,
});

const isAnimationName = (value: unknown): value is AnimationName =>
  typeof value === "string";

const isAnimatableElement = (
  element: Element
): element is AnimatableElement => {
  if (element instanceof HTMLElement) return true;
  if (typeof SVGElement !== "undefined" && element instanceof SVGElement)
    return true;
  return false;
};

const isEasing = (value: unknown): value is Easing => typeof value === "string";

// Type guard for overflow config values (allows preventing text clipping in transitions)
const isTransitionOverflow = (value: unknown): value is TransitionOverflow =>
  value === "hidden" || value === "visible" || value === "clip";

const isAnimationSplitTarget = (
  value: unknown
): value is AnimationSplitTarget =>
  value === "line" || value === "word" || value === "character";

const isKeyframeDefinition = (value: unknown): value is KeyframeDefinition => {
  if (typeof value !== "object" || value === null) return false;
  if (Array.isArray(value)) {
    return value.every((item) => typeof item === "object" && item !== null);
  }
  return true;
};

const isAnimationSettings = (value: unknown): value is AnimationSettings => {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  const { animationName, keyframes, durationMs, delayMs } = candidate;
  return (
    (animationName === undefined || isAnimationName(animationName)) &&
    (keyframes === undefined || isKeyframeDefinition(keyframes)) &&
    (durationMs === undefined || typeof durationMs === "number") &&
    (delayMs === undefined || typeof delayMs === "number")
  );
};

const isTextAnimationConfig = (
  value: unknown
): value is TextAnimationConfig => {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  const { by, staggerMs, animationName, keyframes, delayMs, durationMs } =
    candidate;
  return (
    isAnimationSplitTarget(by) &&
    (delayMs === undefined || typeof delayMs === "number") &&
    (staggerMs === undefined || typeof staggerMs === "number") &&
    (durationMs === undefined || typeof durationMs === "number") &&
    (isAnimationName(animationName) || keyframes !== undefined) &&
    (keyframes === undefined || isKeyframeDefinition(keyframes))
  );
};

const isTransitionConfig = (value: unknown): value is TransitionConfig => {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  const {
    enabled,
    durationMs,
    delayMs,
    easing,
    animateSize,
    overflow,
    textAnimation,
    enter,
    exit,
  } = candidate;
  return (
    (enabled === undefined || typeof enabled === "boolean") &&
    (durationMs === undefined || typeof durationMs === "number") &&
    (delayMs === undefined || typeof delayMs === "number") &&
    (easing === undefined || isEasing(easing)) &&
    (animateSize === undefined || typeof animateSize === "boolean") &&
    (overflow === undefined || isTransitionOverflow(overflow)) &&
    (textAnimation === undefined || isTextAnimationConfig(textAnimation)) &&
    (enter === undefined || isAnimationSettings(enter)) &&
    (exit === undefined || isAnimationSettings(exit))
  );
};

/**
 * Manages and executes complex visual transitions for DOM elements.
 *
 * This class provides methods to smoothly replace elements, handle enter/exit
 * animations, and orchestrate staggered text animations, using the Web
 * Animations API (WAAPI). It is designed to handle dynamic content changes
 * gracefully, including changes in element size.
 */
class TransitionOrchestrator {
  /**
   * Replaces an existing element with a new one, applying coordinated enter,
   * exit, and size animations.
   *
   * The process involves creating a temporary wrapper to contain the animations,
   * measuring the new element's dimensions without causing layout shifts, and
   * then running all animations in parallel. Once complete, the wrapper is
   * replaced by the new element.
   *
   * @param oldElement The original element to be replaced.
   * @param newMarkup The HTML string for the new element.
   * @param config The transition configuration object.
   * @returns A promise that resolves with the new element after the transition completes.
   */
  public replaceWithTransition = async (
    oldElement: AnimatableElement,
    newMarkup: string,
    config: NormalizedTransitionConfig
  ): Promise<Element> => {
    //
    // SETUP
    //
    // The core challenge is to animate smoothly from the old element's state to
    // the new one, including size. To do this, we create a temporary `wrapper`
    // element that will contain both the exiting and entering elements, and
    // animate its dimensions from the old size to the new size.

    // 1. Create a wrapper and a clone of the old element.
    const wrapper = document.createElement("div");
    wrapper.style.position = "relative";
    // Use configurable overflow (default "hidden") - set to "visible" to prevent gradient text clipping
    wrapper.style.overflow = config.overflow;

    const oldRect = oldElement.getBoundingClientRect();
    const oldStyle = window.getComputedStyle(oldElement);
    // `getBoundingClientRect` includes padding and border in width/height, so
    // we only need to copy the margin separately as it's not included.
    wrapper.style.display = oldStyle.display;
    wrapper.style.verticalAlign = oldStyle.verticalAlign;
    wrapper.style.width = `${oldRect.width}px`;
    wrapper.style.height = `${oldRect.height}px`;
    wrapper.style.margin = oldStyle.margin;

    const oldElClone = oldElement.cloneNode(true);

    // This check is necessary because `cloneNode` returns a generic Node.
    if (!(oldElClone instanceof Element) || !isAnimatableElement(oldElClone)) {
      throw new Error("oldElClone is not an AnimatableElement");
    }

    // Position the clone absolutely within the wrapper to allow the new
    // element to be positioned on top of it.
    oldElClone.style.position = "absolute";
    oldElClone.style.top = "0";
    oldElClone.style.left = "0";
    oldElClone.style.width = "100%";
    oldElClone.style.height = "100%";
    oldElClone.style.margin = "0";
    oldElClone.style.pointerEvents = "none";
    wrapper.appendChild(oldElClone);

    // 2. Create the new element from the provided markup.
    const tempContainer = document.createElement("div");
    tempContainer.innerHTML = newMarkup;
    const newElement = tempContainer.firstElementChild;
    if (
      !newElement ||
      !(newElement instanceof HTMLElement || newElement instanceof SVGElement)
    ) {
      // Fallback: if markup is empty or invalid, just animate the old element out.
      const exitAnim = this.exit(oldElement, config);
      await exitAnim.finished;
      return oldElement;
    }

    const originalStyle = newElement.style.cssText;

    // We must move text animation to after the element is in the DOM for an
    // accurate style computation, which is critical for gradient text.
    // if (config.textAnimation) {
    //   this.applyTextAnimationToNode(newElement, config.textAnimation, config)
    // }

    //
    // MEASUREMENT
    //
    // To avoid the "overshoot and snap" problem, we must measure the new
    // element in a context that perfectly replicates its final position in the
    // DOM. Simply appending it to the body is not enough, as it won't inherit
    // the correct styles (e.g., flex, grid) from its parent.

    // 3. Perform a high-fidelity, contextual off-screen measurement.
    const parent = oldElement.parentNode;
    let newRect: DOMRect;

    // We create a clone of the parent element to ensure that the new element is
    // measured in a context that is identical to its final destination. This is
    // crucial for accurate dimensions, especially in flexbox or grid layouts.
    if (parent instanceof HTMLElement) {
      // Create a shallow clone of the parent; we don't need its children.
      const parentClone = parent.cloneNode(false) as HTMLElement;
      const parentStyles = window.getComputedStyle(parent);
      // Copy all computed styles from the original parent to the clone. This
      // is the key to an accurate measurement. A fallback is included for
      // Firefox, which does not support `cssText` on `CSSStyleDeclaration`.
      const cssText = parentStyles.cssText
        ? parentStyles.cssText
        : Array.from(parentStyles).reduce(
            (str, prop) =>
              `${str}${prop}:${parentStyles.getPropertyValue(prop)};`,
            ""
          );

      parentClone.style.cssText = cssText;
      // Position the clone off-screen to avoid affecting the layout.
      parentClone.style.position = "absolute";
      parentClone.style.top = "-9999px";
      parentClone.style.left = "-9999px";
      parentClone.style.visibility = "hidden";
      parentClone.style.pointerEvents = "none";

      parentClone.appendChild(newElement);
      document.body.appendChild(parentClone);

      // Text animations can affect element dimensions, so they must be applied
      // before we take the final measurement.
      if (config.textAnimation) {
        this.applyTextAnimationToNode(newElement, config.textAnimation, config);
      }

      newRect = newElement.getBoundingClientRect();
      document.body.removeChild(parentClone);
    } else {
      // Fallback to a less accurate method if the parent is not an HTMLElement.
      // This is a safety measure for edge cases where the parent might be a
      // DocumentFragment or another non-element node.
      const offscreenContainer = document.createElement("div");
      offscreenContainer.style.position = "absolute";
      offscreenContainer.style.top = "-9999px";
      offscreenContainer.style.left = "-9999px";
      offscreenContainer.style.visibility = "hidden";

      offscreenContainer.appendChild(newElement);
      document.body.appendChild(offscreenContainer);

      if (config.textAnimation) {
        this.applyTextAnimationToNode(newElement, config.textAnimation, config);
      }

      newRect = newElement.getBoundingClientRect();
      document.body.removeChild(offscreenContainer);
    }

    //
    // ANIMATION
    //
    // With accurate measurements, we can now set up the final state and run the
    // animations for the exit, enter, and size transitions in parallel.

    // 4. Setup the new element for its entrance animation.
    newElement.style.cssText = originalStyle; // Restore original styles
    newElement.style.visibility = "";
    newElement.style.position = "absolute";
    newElement.style.top = "0";
    newElement.style.left = "0";
    newElement.style.width = `${newRect.width}px`;
    newElement.style.height = `${newRect.height}px`;
    newElement.style.margin = "0";
    newElement.style.opacity = "0"; // Start transparent for entry animation

    wrapper.appendChild(newElement);

    // Replace the original element with the wrapper in the DOM.
    oldElement.replaceWith(wrapper);

    // 5. Animate with the Web Animations API (WAAPI).
    const exitDelay = config.exit?.delayMs ?? config.delayMs;
    const exitDuration = config.exit?.durationMs ?? config.durationMs;
    const enterDelay = config.enter?.delayMs ?? config.delayMs;
    const enterDuration = config.enter?.durationMs ?? config.durationMs;

    const animations: Animation[] = [];

    // Exit animation
    const exitKeyframes = this.resolveKeyframes(config.exit?.keyframes);
    const exitAnim = oldElClone.animate(exitKeyframes, {
      duration: exitDuration,
      delay: exitDelay,
      easing: config.easing,
      fill: "forwards",
    });
    animations.push(exitAnim);

    // Enter animation
    const enterKeyframes = this.resolveKeyframes(config.enter?.keyframes);
    const enterAnim = newElement.animate(enterKeyframes, {
      duration: enterDuration,
      delay: enterDelay,
      easing: config.easing,
      fill: "forwards",
    });
    animations.push(enterAnim);

    // Size animation
    if (config.animateSize) {
      const sizeAnim = wrapper.animate(
        [
          { width: `${oldRect.width}px`, height: `${oldRect.height}px` },
          { width: `${newRect.width}px`, height: `${newRect.height}px` },
        ],
        {
          duration: enterDuration, // Match the enter animation
          delay: enterDelay,
          easing: config.easing,
          fill: "forwards",
        }
      );
      animations.push(sizeAnim);
    }

    // 6. Wait for all animations to finish.
    await Promise.all(animations.map((a) => a.finished));

    //
    // CLEANUP
    //
    // Once animations are complete, we replace the wrapper with the new element
    // and restore its original styles, leaving the DOM in a clean state.

    // 7. Cleanup and final DOM replacement.
    if (wrapper.parentNode) {
      wrapper.replaceWith(newElement);
    }
    newElement.style.cssText = originalStyle; // Restore original styles
    oldElClone.remove();

    return newElement;
  };

  /**
   * Triggers an enter animation on a given element.
   * @param element The element to animate.
   * @param config The transition configuration.
   * @returns The Animation object for the enter transition.
   */
  public enter = (
    element: AnimatableElement,
    config: NormalizedTransitionConfig
  ): Animation => {
    const enterDelay = config.enter?.delayMs ?? config.delayMs;
    const enterDuration = config.enter?.durationMs ?? config.durationMs;

    const keyframes = this.resolveKeyframes(config.enter?.keyframes);
    return element.animate(keyframes, {
      duration: enterDuration,
      delay: enterDelay,
      easing: config.easing,
      fill: "forwards",
    });
  };

  /**
   * Triggers an exit animation on a given element.
   * @param element The element to animate.
   * @param config The transition configuration.
   * @returns The Animation object for the exit transition.
   */
  public exit = (
    element: AnimatableElement,
    config: NormalizedTransitionConfig
  ): Animation => {
    const exitDelay = config.exit?.delayMs ?? config.delayMs;
    const exitDuration = config.exit?.durationMs ?? config.durationMs;

    const keyframes = this.resolveKeyframes(config.exit?.keyframes);
    return element.animate(keyframes, {
      duration: exitDuration,
      delay: exitDelay,
      easing: config.easing,
      fill: "forwards",
    });
  };

  /**
   * Resolves the keyframes to be used for an animation, prioritizing custom
   * definitions over presets.
   * @param definition An optional custom keyframe definition.
   * @returns An array of keyframes for use with WAAPI.
   */
  private resolveKeyframes = (definition?: KeyframeDefinition): Keyframe[] => {
    if (definition) {
      return Array.isArray(definition) ? definition : [definition];
    }
    return [];
  };

  /**
   * Traverses a DOM node to find text nodes and applies a staggered animation
   * to them by splitting the text into lines, words, or characters.
   *
   * Each segment is wrapped in a `<span>` and animated individually to create
   * a "typewriter" or "reveal" effect.
   *
   * @param element The root element to traverse for text nodes.
   * @param config The text animation configuration.
   * @param transitionConfig The parent transition configuration for timing.
   */
  private applyTextAnimationToNode = (
    element: Element,
    config: TextAnimationConfig,
    transitionConfig: NormalizedTransitionConfig
  ) => {
    const initialDelay = config.delayMs ?? 0;
    const computedStyle = window.getComputedStyle(element);
    const isBackgroundClippedToText =
      computedStyle.getPropertyValue("background-clip") === "text" ||
      computedStyle.getPropertyValue("-webkit-background-clip") === "text";

    // This recursive function will traverse the DOM, keeping track of the animation delay.
    const traverseAndAnimate = (
      node: Node,
      currentDelay: number,
      isBackgroundClippedToText: boolean
    ): number => {
      // If it's a text node, split and animate it.
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent ?? "";
        if (text.trim().length === 0) {
          return currentDelay; // Don't animate whitespace-only nodes
        }

        // ------------
        // Special handling for character animation to prevent breaking words
        // ------------
        if (config.by === "character") {
          const words = text.split(/(\s+)/);
          const fragment = document.createDocumentFragment();
          let delay = currentDelay;

          words.forEach((word) => {
            if (word.length === 0) return;

            // Preserve whitespace as plain text nodes
            if (word.trim().length === 0) {
              fragment.appendChild(document.createTextNode(word));
              return;
            }

            // Wrap words in a span that prevents line breaks inside the word
            const wordWrapper = document.createElement("span");
            wordWrapper.style.display = "inline-block";
            wordWrapper.style.whiteSpace = "nowrap";

            if (isBackgroundClippedToText) {
              // Ensure the wrapper propagates background properties
              wordWrapper.style.setProperty("background", "inherit");
              wordWrapper.style.setProperty("-webkit-background-clip", "text");
              wordWrapper.style.setProperty("background-clip", "text");
              wordWrapper.style.setProperty("color", "transparent");
            }

            const chars = word.split("");
            chars.forEach((char) => {
              const span = document.createElement("span");
              span.textContent = char;
              span.style.display = "inline-block";
              span.style.opacity = "0";

              if (isBackgroundClippedToText) {
                span.style.setProperty("background", "inherit");
                span.style.setProperty("-webkit-background-clip", "text");
                span.style.setProperty("background-clip", "text");
                span.style.setProperty("color", "transparent");

                // Add padding to prevent clipping of descenders/gradients
                span.style.paddingBottom = "0.1em";
                span.style.marginBottom = "-0.1em";
              }

              const keyframes = this.resolveKeyframes(config.keyframes);
              const duration =
                config.durationMs ?? transitionConfig.durationMs ?? 300;
              const easing = transitionConfig.easing ?? "ease";

              // Use setTimeout for stagger to ensure animations start after
              // synchronous DOM operations complete. This prevents corruption
              // when elements are temporarily removed from DOM during
              // off-screen measurement in replaceWithTransition.
              setTimeout(() => {
                const animation = span.animate(keyframes, {
                  duration,
                  easing,
                  fill: "forwards",
                });

                animation.addEventListener("finish", () => {
                  animation.commitStyles();
                  animation.cancel();
                });
              }, delay);

              wordWrapper.appendChild(span);
              delay += config.staggerMs ?? 50;
            });

            fragment.appendChild(wordWrapper);
          });

          node.parentNode?.replaceChild(fragment, node);
          return delay;
        }

        let segments: string[];
        switch (config.by) {
          case "word":
            segments = text.split(/(\s+)/); // Preserve spaces as segments
            break;
          case "line":
            segments = text.split("\n");
            break;
          default:
            segments = text.split(""); // Fallback (shouldn't happen with types)
        }

        const fragment = document.createDocumentFragment();
        let delay = currentDelay;

        segments.forEach((segment) => {
          if (segment.length === 0) return;
          const isWhitespace = segment.trim().length === 0;

          // Preserve whitespace as plain text nodes
          if (isWhitespace) {
            fragment.appendChild(document.createTextNode(segment));
            return;
          }

          const span = document.createElement("span");
          span.textContent = segment;
          span.style.display = "inline-block";
          span.style.opacity = "0";

          if (isBackgroundClippedToText) {
            span.style.setProperty("background", "inherit");
            span.style.setProperty("-webkit-background-clip", "text");
            span.style.setProperty("background-clip", "text");
            span.style.setProperty("color", "transparent");
            span.style.setProperty("will-change", "opacity, transform");
          }

          const keyframes = this.resolveKeyframes(config.keyframes);
          const duration =
            config.durationMs ?? transitionConfig.durationMs ?? 300;
          const easing = transitionConfig.easing ?? "ease";

          // Use setTimeout for stagger to ensure animations start after
          // synchronous DOM operations complete. This prevents corruption
          // when elements are temporarily removed from DOM during
          // off-screen measurement in replaceWithTransition.
          setTimeout(() => {
            const animation = span.animate(keyframes, {
              duration,
              easing,
              fill: "forwards",
            });

            animation.addEventListener("finish", () => {
              animation.commitStyles();
              animation.cancel();
            });
          }, delay);

          fragment.appendChild(span);
          delay += config.staggerMs ?? 50;
        });

        node.parentNode?.replaceChild(fragment, node);
        return delay; // Return the updated delay
      }

      // If it's an element node, traverse its children.
      if (node.nodeType === Node.ELEMENT_NODE) {
        let delay = currentDelay;
        // We must work with a static copy of child nodes, as the collection will be modified.
        const children = Array.from(node.childNodes);
        for (const child of children) {
          delay = traverseAndAnimate(child, delay, isBackgroundClippedToText);
        }
        return delay; // Return the final delay after traversing all children
      }

      // For any other node type, just continue with the same delay.
      return currentDelay;
    };

    // Start the traversal from the root element.
    traverseAndAnimate(element, initialDelay, isBackgroundClippedToText);
  };
}

/**
 * Response from the cue-card-config API endpoint.
 * Used to determine if a CueCard should be mounted and with what config.
 */
interface CueCardConfigResponse {
  enabled: boolean;
  config?: {
    theme?: "glass" | "light" | "dark";
    position?: "top-center" | "top-right";
    direction?: "top-to-bottom" | "right-to-left";
    fields: Array<{
      name: string;
      label: string;
      placeholder?: string;
      type?: "text" | "email" | "tel" | "url";
      required?: boolean;
      pattern?: string;
      minLength?: number;
      maxLength?: number;
      errorMessage?: string;
    }>;
    enableFocusMode?: boolean;
    showWatermark?: boolean;
    showKeyboardHints?: boolean;
    enableLauncher?: boolean;
    customTheme?: {
      fontFamily?: string;
      backgroundColor?: string;
      borderColor?: string;
      textColor?: string;
      subtitleColor?: string;
      primaryColor?: string;
      primaryTextColor?: string;
      borderRadius?: string;
    };
    textOverrides?: {
      titleResting?: string;
      titleSuccess?: string;
      titleError?: string;
      subtitleResting?: string;
      subtitleSuccess?: string;
      subtitleError?: string;
      btnResting?: string;
      btnSuccess?: string;
      btnLoading?: string;
      btnError?: string;
      launcherLabel?: string;
      launcherLabelSuccess?: string;
      launcherLabelDeanonymized?: string;
      launcherLabelDeanonymizedSuccess?: string;
    };
    enableUndoToggle?: boolean;
    deanonymization?: {
      provider: "Kenobi";
      timeoutMs?: number;
      logoUrlTemplate?: string;
      snitcher?: {
        spotterToken: string;
        profileId: string;
        apiEndpoint?: string;
        cdn?: string;
        namespace?: string;
      };
    };
  };
}

type IdentityResolutionDetail = {
  type?: "business" | "isp" | "unknown";
  domain?: string | null;
  company?: {
    name?: string;
    domain?: string;
  };
};

type PersonalizationApiResult = {
  transformations?: Transformation[];
  metadata?: Record<string, unknown>;
};

type PersonalizationPredictionSource = "deanonymized";

type PreparedPersonalization = {
  input: Record<string, string>;
  result: PersonalizationApiResult;
  source?: PersonalizationPredictionSource;
};

type PersonalizationRequestOptions = {
  notifySlack?: boolean;
  notifyOnly?: boolean;
  predictionSource?: PersonalizationPredictionSource;
  prefetchedTransformations?: Transformation[];
  prefetchedMetadata?: Record<string, unknown>;
};

const IDENTITY_RESOLVED_EVENT = "kenobi:identity-resolved";
const DEFAULT_DEANON_TIMEOUT_MS = 1600;

const isCueCardConfigResponse = (
  value: unknown
): value is CueCardConfigResponse => {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return typeof candidate.enabled === "boolean";
};

/**
 * Configuration options for the Kenobi instance.
 *
 * When `publicKey` is provided, Kenobi automatically:
 * 1. Mounts a CueCard UI for visitor input
 * 2. Handles form submission
 * 3. Calls the personalization API
 * 4. Applies transformations to the page
 *
 * Future: CueCard config will be fetched per-org from the database.
 * For now, default config matching the template editor is used.
 */
interface KenobiConfig {
  /**
   * Your Kenobi public key (pk_live_xxx or pk_test_xxx).
   * When provided, a CueCard is automatically mounted and the
   * personalization flow is handled end-to-end.
   */
  publicKey?: string;
  apiHost?: string;
  siteVersionId?: string;
  debug?: boolean;
  autoTransform?: boolean;
  transitionDefaults?: TransitionConfig;
  /**
   * When true, skips automatic CueCard mounting even when publicKey is set.
   * Use this when you want to manage the CueCard externally but still use
   * the SDK's personalize() method.
   */
  skipCueCardAutoMount?: boolean;
}

interface ApplyTransformationsOpts {
  shouldConsolidate?: boolean;
}

interface UndoOperationBase {
  type: "Remove" | "Replace" | "SetInput";
  ref: Element;
}

interface UndoOperationRemove extends UndoOperationBase {
  type: "Remove";
}

interface UndoOperationReplace extends UndoOperationBase {
  type: "Replace";
  content: string;
}

interface UndoInputElementSet extends UndoOperationBase {
  type: "SetInput";
  content: string;
}

type UndoOperation =
  | UndoOperationRemove
  | UndoOperationReplace
  | UndoInputElementSet;

interface ChangeStackEntry {
  undoOpts: UndoOperation;
}

export type TransformationAction =
  | "Replace"
  | "InsertBefore"
  | "InsertAfter"
  | "Delete"
  | "SetFormField";

export interface Transformation {
  identifier: string;
  markup: string;
  selectorGuesses: string[];
  action: TransformationAction;
  transition?: TransitionConfig;
}

export interface PersonalizeApiResponse {
  transformations?: Transformation[];
  metadata?: Record<string, unknown>;
}

export const TRANSFORMATION_ACTIONS: ReadonlySet<TransformationAction> =
  new Set(["Replace", "InsertBefore", "InsertAfter", "Delete", "SetFormField"]);

const isString = (value: unknown): value is string => typeof value === "string";

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => isString(item));

const isTransformationAction = (
  value: unknown
): value is TransformationAction =>
  typeof value === "string" &&
  TRANSFORMATION_ACTIONS.has(value as TransformationAction);

const isTransformation = (value: unknown): value is Transformation => {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    isString(candidate.identifier) &&
    typeof candidate.markup === "string" &&
    isStringArray(candidate.selectorGuesses) &&
    isTransformationAction(candidate.action) &&
    (candidate.transition === undefined ||
      isTransitionConfig(candidate.transition))
  );
};

const parseTransformationsResponse = (payload: unknown): Transformation[] => {
  if (typeof payload !== "object" || payload === null) {
    throw new Error("Invalid response: expected object with data array");
  }

  const data = (payload as Record<string, unknown>).data;
  if (!Array.isArray(data)) {
    throw new Error("Invalid response: data must be an array");
  }

  data.forEach((item, index) => {
    if (!isTransformation(item)) {
      throw new Error(`Invalid transformation at index ${index}`);
    }
  });

  return data;
};

class Kenobi {
  private config: KenobiConfig;
  private changeStack: Map<string, ChangeStackEntry> = new Map<
    string,
    ChangeStackEntry
  >();
  private visitorKey: string | null = null;
  private orchestrator: TransitionOrchestrator;
  private cueCardInstance: CueCard | null = null;
  private preparedPersonalization: PreparedPersonalization | null = null;
  private isPreparingPersonalization = false;
  private preparePersonalizationPromise: Promise<PersonalizationApiResult | null> | null =
    null;
  private deanonymizationPrefetchCleanup: (() => void) | null = null;
  //
  private currentPath: string;
  private currentUrl: string;
  private navTicking = false;
  private transitionDefaults: NormalizedTransitionConfig;

  // Undo toggle state
  private lastAppliedTransformations: Transformation[] = [];
  public isPersonalized = false;

  constructor(config?: KenobiConfig) {
    this.config = {
      apiHost: "https://kenobi.ai/api",
      debug: false,
      autoTransform: true,
      ...config,
    };

    this.transitionDefaults = createNormalizedTransitionConfig(
      this.config.transitionDefaults
    );
    this.orchestrator = new TransitionOrchestrator();

    this.syncVisitorKey();

    this.currentPath = this.getCurrentPath();
    this.currentUrl = this.getCurrentUrl();

    try {
      this.initNavigationListener();
    } catch (error) {
      this.log(
        "error",
        "Failed to initialize navigation listener with error:",
        error
      );
    }
    this.log("debug", "Kenobi initialized");

    if (this.config.autoTransform) {
      this.log("debug", "Auto-transform is requested... Starting...");
      void this.startAutoTransform();
    }

    // Auto-mount CueCard when publicKey is provided (unless skipCueCardAutoMount is set)
    if (this.config.publicKey && !this.config.skipCueCardAutoMount) {
      this.log("debug", "Public key provided, checking for active template...");
      void this.initCueCard();
    }
  }

  private startAutoTransform = async () => {
    try {
      this.syncChangeStack();

      // All the waits!
      await this.waitForLoaded();

      if (this.isFramer()) {
        await this.waitForFrames();
        await this.waitForIdle();
        await this.waitForQuietDom(100, 1_000);
      }

      // Go go go!
      await this.transform();
    } catch (error) {
      this.log("error", "Auto-transformation flow failed with error:", error);
    }
  };

  public transform = async () => {
    try {
      const startTime = new Date();
      this.log("debug", "Starting transform");

      if (this.visitorKey === null) {
        this.log(
          "debug",
          `Couldn't find a visitor copy external ID with query param key "${VISITOR_QUERY_PARAM_KEY}"`,
          "Exiting..."
        );
        return;
      }

      let transformations: Transformation[] = [];

      try {
        transformations = await this.getTransformations(
          this.currentPath,
          this.visitorKey
        );
      } catch (error) {
        this.log(
          "error_detailed",
          "An error occurred _getting_ transformations",
          error
        );
      }

      try {
        await this.applyTransformations(transformations);
      } catch (error) {
        this.log(
          "error_detailed",
          "An error occurred _applying_ transformations",
          error
        );
      }

      // END!
      const endTime = new Date();
      const fnDuration = this.getDurationInMilliseconds(startTime, endTime);
      this.log(
        "debug",
        `Transformation complete, took ${this.formatThousands(fnDuration)} ms`
      );
    } catch (error) {
      this.log(
        "error",
        "An unexpected, top-level error occurred during transformation",
        error
      );
    }
  };

  /*
  ----
  --   UTILITIES
  ----
  */
  private log = (
    level: "debug" | "info" | "warn" | "error" | "error_detailed",
    message: string,
    ...rest: unknown[]
  ) => {
    const version =
      typeof window.__KENOBI_VERSION__ !== "undefined"
        ? window.__KENOBI_VERSION__
        : "dev";
    const masthead = `[Kenobi ◔] (v${version})`;
    const text = `${masthead} [${level.toLocaleUpperCase()}] :: ${message}`;
    switch (level) {
      case "debug":
        if (this.config.debug) console.log(text, ...rest);
        break;
      case "info":
        console.log(text, ...rest);
        break;
      case "warn":
        console.warn(text, ...rest);
        break;
      case "error_detailed":
        console.trace(...rest);
      // fallthrough
      case "error":
        console.error(text, ...rest);
        break;
    }
  };

  private resolveTransitionConfig = (
    override?: TransitionConfig
  ): NormalizedTransitionConfig =>
    mergeTransitionConfig(this.transitionDefaults, override);

  private retrieveElement = (selectorGuesses: string[]) => {
    if (
      !Array.isArray(selectorGuesses) ||
      selectorGuesses.some((s) => typeof s !== "string")
    ) {
      throw new Error(
        `Invalid selectorGuesses payload received: expected array of strings but got ${JSON.stringify(
          selectorGuesses
        )}`
      );
    }

    for (const selector of selectorGuesses) {
      const candidates = document.querySelectorAll(selector);
      if (candidates.length === 0) continue;

      // Single match - return directly
      if (candidates.length === 1) return candidates[0];

      // Multiple matches - filter to visible desktop elements and prefer above-fold
      const visible = Array.from(candidates).filter((el) => {
        const rect = el.getBoundingClientRect();
        const style = getComputedStyle(el);
        return (
          style.display !== "none" &&
          style.visibility !== "hidden" &&
          rect.width > 0 &&
          rect.height > 0
        );
      });

      if (visible.length === 0) continue;

      // Prefer element closest to viewport top (hero area)
      const sorted = visible.sort(
        (a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top
      );

      this.log(
        "debug",
        `Selector "${selector}" matched ${
          candidates.length
        } elements, selected topmost visible one at y=${
          sorted[0].getBoundingClientRect().top
        }`
      );

      return sorted[0];
    }
    throw new Error(
      `Could not find an element in the document with any of these selector patterns: ${selectorGuesses.join(
        ", "
      )}`
    );
  };

  private getQueryParam = (key: string) => {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get(key);
  };

  private getCurrentPath = () => window.location.pathname;
  private getCurrentUrl = () =>
    window.location.pathname + window.location.search + window.location.hash;

  private getDurationInMilliseconds = (start: Date, end: Date) => +end - +start;

  private formatThousands = (number: number) =>
    new Intl.NumberFormat("en-US").format(number);

  public setDebug = (value: boolean) => (this.config.debug = value);

  private isInputElement = (
    el: Element
  ): el is HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement => {
    if (
      !(el instanceof HTMLInputElement) &&
      !(el instanceof HTMLTextAreaElement) &&
      !(el instanceof HTMLSelectElement)
    )
      return false;

    return true;
  };

  /*
  ----
  --   UTILITIES // PAGE WAITING
  ----
  */

  private waitForFrames = () =>
    // waits 2 animation frames
    new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

  private waitForIdle = (timeout = 1000) => {
    return new Promise<void>((r) =>
      "requestIdleCallback" in window
        ? window.requestIdleCallback(() => r(), { timeout })
        : setTimeout(r, 50)
    );
  };

  private waitForLoaded = () => {
    if (document.readyState === "complete") return Promise.resolve();
    return new Promise<void>((r) =>
      window.addEventListener("load", () => r(), { once: true })
    );
  };

  private waitForQuietDom = (maxWaitMs: number, quietMs: number) => {
    return new Promise<void>((resolve) => {
      let last = performance.now();
      const mo = new MutationObserver(() => {
        last = performance.now();
      });
      mo.observe(document.documentElement, {
        subtree: true,
        childList: true,
        attributes: true,
        characterData: true,
      });
      const deadline = performance.now() + maxWaitMs;
      (function tick() {
        const now = performance.now();
        if (now - last >= quietMs) {
          mo.disconnect();
          resolve();
          return;
        }
        if (now >= deadline) {
          mo.disconnect();
          resolve();
          return;
        }
        requestAnimationFrame(tick);
      })();
    });
  };

  /*
  ----
  --   UTILITIES // PLATFORMS
  ----
  */

  private isFramer = () => {
    const scripts = Array.from(document.scripts).map((s) => s.src || "");
    const conditions = [
      "__framer_events" in window,
      document.querySelector(
        "[data-framer-root],[data-framer-component],[data-framer-page]"
      ),
      scripts.some((src) => /framerusercontent\.com\/sites/i.test(src)),
    ];
    return conditions.some((c) => !!c);
  };

  /*
  ----
  --   VISITOR STATE MANAGEMENT
  ----
  */
  private syncVisitorKey = () => {
    try {
      const queryParamValue = this.getQueryParam(VISITOR_QUERY_PARAM_KEY);
      if (queryParamValue) {
        this.log(
          "debug",
          `Query param value found (${queryParamValue}), pushing into session storage`
        );
        sessionStorage.setItem(VISITOR_SESSION_STORAGE_KEY, queryParamValue);
        this.visitorKey = queryParamValue;
      } else {
        this.visitorKey = sessionStorage.getItem(VISITOR_SESSION_STORAGE_KEY);
      }
    } catch (error) {
      this.log("error", "Failed to sync visitor key with error:", error);
    }
  };

  private initNavigationListener = () => {
    // avoid double-hooking if multiple instances
    if ("__KENOBI_NAV_HOOKED__" in window) return;
    window.__KENOBI_NAV_HOOKED__ = true;

    const schedule = (src: string) => {
      if (this.navTicking) return;
      this.navTicking = true;
      queueMicrotask(() => {
        this.navTicking = false;
        const nextUrl = this.getCurrentUrl();
        if (nextUrl !== this.currentUrl) {
          this.currentUrl = nextUrl;
          this.currentPath = this.getCurrentPath(); // still just pathname for your API
          this.log("debug", `URL changed (${src}) → ${location.href}`);
          if (this.config.autoTransform) {
            this.undoAll();
            void this.startAutoTransform();
          }
          // Sync CueCard visibility based on new path
          this.syncCueCardVisibility();
        }
      });
    };

    // 1) Navigation API (Chromium, Safari TP)
    if ("navigation" in window) {
      const nav = window.navigation;
      if (
        nav &&
        "addEventListener" in nav &&
        typeof nav.addEventListener === "function"
      ) {
        nav.addEventListener("navigate", () => schedule("navigation:navigate"));
        nav.addEventListener("currententrychange", () =>
          schedule("navigation:entrychange")
        );
        nav.addEventListener?.("navigatesuccess", () =>
          schedule("navigation:success")
        );
      }
    }

    // 2) History API hooks
    const origPush = history.pushState.bind(history);
    const origRepl = history.replaceState.bind(history);
    history.pushState = ((...args: Parameters<History["pushState"]>) => {
      const ret = origPush(...args);
      schedule("pushState");
      return ret;
    }) as History["pushState"];
    history.replaceState = ((...args: Parameters<History["replaceState"]>) => {
      const ret = origRepl(...args);
      schedule("replaceState");
      return ret;
    }) as History["replaceState"];

    // 3) Browser events
    window.addEventListener("popstate", () => schedule("popstate"));
    window.addEventListener("hashchange", () => schedule("hashchange"));

    // 4) Anchor clicks (covers some router edge cases)
    document.addEventListener(
      "click",
      (e) => {
        const t = e.target as Element | null;
        const a = t && (t.closest?.("a[href]") as HTMLAnchorElement | null);
        if (!a) return;
        if (a.target && a.target !== "_self") return;
        const url = new URL(a.href, location.href);
        if (url.origin !== location.origin) return;
        // router may pushState on next tick
        setTimeout(() => schedule("click"), 0);
      },
      true
    );

    // 5) Cheap last-resort poller (handles exotic SPA routers)
    setInterval(() => {
      const next = this.getCurrentUrl();
      if (next !== this.currentUrl) schedule("poll");
    }, 1_000);
  };

  /*
  ----
  --   DATA RETRIEVAL
  ----
  */
  private getTransformations = async (
    path: string,
    visitorCopyId: string
  ): Promise<Transformation[]> => {
    const searchParams = new URLSearchParams({
      path,
      // siteId: this.config.siteId,
      // publicKey: this.config.publicKey,
    });

    const rawResponse = await fetch(
      `${
        this.config.apiHost
      }/v1/visitor-copy-transformations/${visitorCopyId}?${searchParams.toString()}`
    );
    const responseJson = await rawResponse.json();

    if (!rawResponse.ok) {
      throw new Error(
        `Network error (status ${
          rawResponse.status
        }) with json body: ${JSON.stringify(responseJson, null, 2)}`
      );
    }

    return parseTransformationsResponse(responseJson);
  };

  private validateTransitionPreconditions = (
    identifier: string,
    markup: string,
    transitionConfig: NormalizedTransitionConfig
  ): boolean => {
    if (transitionConfig.enabled) {
      const tempContainer = document.createElement("div");
      tempContainer.innerHTML = markup;
      const prospectiveElement = tempContainer.firstElementChild;
      if (!prospectiveElement || !isAnimatableElement(prospectiveElement)) {
        this.log(
          "warn",
          `Transformation "${identifier}" was skipped: A transition was requested, but the element is not animatable.`
        );
        return false; // Indicates failure, caller should hard-exit.
      }
    }
    return true; // Indicates success
  };

  /*
  ----
  --   DOM + TRANSFORMATION MANIPULATION
  ----
  */
  public applyTransformations = async (
    transformations: Transformation[],
    opts?: ApplyTransformationsOpts
  ) => {
    this.log("debug", `Starting ${transformations.length} transformations`);

    // "Consolidation" mode traverses the existing changestack and diffs it
    // against the provided transformations, so that only those provided here
    // and now will apply (all others will be undone).
    if (opts?.shouldConsolidate) {
      this.changeStack.forEach((_, key) => {
        const matchingTransformation = transformations.find(
          (tran) => tran.identifier === key
        );
        if (matchingTransformation) {
          this.undo(key);
        }
      });
    }

    for (const { identifier, ...transformation } of transformations) {
      try {
        if (this.changeStack.get(identifier)) {
          throw new Error("Transformation already exists in stack");
        }

        const cursor = this.retrieveElement(transformation.selectorGuesses);

        switch (transformation.action) {
          case "Replace": {
            const originalOuterHtml = cursor.outerHTML;
            const transitionConfig = this.resolveTransitionConfig(
              transformation.transition
            );

            if (
              transitionConfig.enabled &&
              isAnimatableElement(cursor) &&
              "animate" in cursor
            ) {
              const newElement = await this.orchestrator.replaceWithTransition(
                cursor,
                transformation.markup,
                transitionConfig
              );
              this.setOrUpdateReplaceChangeRef(
                identifier,
                originalOuterHtml,
                newElement
              );
            } else {
              morphdom(cursor, transformation.markup, {});
              this.setOrUpdateReplaceChangeRef(
                identifier,
                originalOuterHtml,
                cursor
              );
            }

            if (cursor.nodeName === "VIDEO")
              (cursor as HTMLVideoElement).play();

            break;
          }

          case "InsertBefore": {
            const transitionConfig = this.resolveTransitionConfig(
              transformation.transition
            );

            // check for animatability before any mutations.
            if (
              !this.validateTransitionPreconditions(
                identifier,
                transformation.markup,
                transitionConfig
              )
            ) {
              break;
            }

            cursor.insertAdjacentHTML("beforebegin", transformation.markup);
            const ref = cursor.previousElementSibling;
            if (ref === null) throw new Error("Element wasn't created");
            ref.setAttribute(TRANSFORMATION_DATA_ATTR_KEY, identifier);

            if (transitionConfig.enabled && isAnimatableElement(ref)) {
              const originalStyle = ref.style.cssText;
              ref.style.opacity = "0";
              const anim = this.orchestrator.enter(ref, {
                ...transitionConfig,
                delayMs:
                  transitionConfig.enter?.delayMs ?? transitionConfig.delayMs,
              });
              anim.addEventListener(
                "finish",
                () => {
                  ref.style.cssText = originalStyle;
                },
                {
                  once: true,
                }
              );
            }

            this.changeStack.set(identifier, {
              undoOpts: {
                type: "Remove",
                ref,
              },
            });
            break;
          }

          case "InsertAfter": {
            const transitionConfig = this.resolveTransitionConfig(
              transformation.transition
            );

            // check for animatability before any mutations.
            if (
              !this.validateTransitionPreconditions(
                identifier,
                transformation.markup,
                transitionConfig
              )
            ) {
              break;
            }

            cursor.insertAdjacentHTML("afterend", transformation.markup);
            const ref = cursor.nextElementSibling;
            if (ref === null) throw new Error("Element wasn't created");
            ref.setAttribute(TRANSFORMATION_DATA_ATTR_KEY, identifier);

            if (transitionConfig.enabled && isAnimatableElement(ref)) {
              const originalStyle = ref.style.cssText;
              ref.style.opacity = "0";
              const anim = this.orchestrator.enter(ref, {
                ...transitionConfig,
                delayMs:
                  transitionConfig.enter?.delayMs ?? transitionConfig.delayMs,
              });
              anim.addEventListener(
                "finish",
                () => {
                  ref.style.cssText = originalStyle;
                },
                {
                  once: true,
                }
              );
            }

            this.changeStack.set(identifier, {
              undoOpts: {
                type: "Remove",
                ref,
              },
            });
            break;
          }

          case "Delete": {
            const originalOuterHtml = cursor.outerHTML;
            morphdom(
              cursor,
              `<div style="display: none;" data-kenobi-delete-marker="true"></div>`,
              {}
            );
            cursor.setAttribute(TRANSFORMATION_DATA_ATTR_KEY, identifier);
            this.changeStack.set(identifier, {
              undoOpts: {
                type: "Replace",
                ref: cursor,
                content: originalOuterHtml,
              },
            });
            break;
          }

          case "SetFormField": {
            if (!this.isInputElement(cursor))
              throw new Error("Element targeted is not of a supported type");
            const originalValue = cursor.value;
            this.setInputValue(cursor, transformation.markup);
            cursor.setAttribute(TRANSFORMATION_DATA_ATTR_KEY, identifier);
            this.changeStack.set(identifier, {
              undoOpts: {
                type: "SetInput",
                ref: cursor,
                content: originalValue,
              },
            });
          }
        }
      } catch (err) {
        this.log(
          "warn",
          `Unable to apply transformation with identifier "${identifier}"`,
          err
        );
      }
    }

    // Store transformations for redo and mark as personalized
    this.lastAppliedTransformations = transformations;
    this.isPersonalized = true;
    this.updateCueCardPersonalizationState();
  };

  /**
   * Creates or updates an entry in the change stack for a "Replace" action,
   * enabling undo functionality.
   *
   * This function ensures that for any given transformation identifier, there is a
   * corresponding undo record. It handles both the initial creation of this
   * record and updates to it if a transformation is re-applied, for instance,
   * after a transition.
   *
   * @param identifier The unique identifier for the transformation.
   * @param originalOuterHtml The original outerHTML of the element before replacement.
   * @param ref The new element that has replaced the original one.
   */
  private setOrUpdateReplaceChangeRef = (
    identifier: string,
    originalOuterHtml: string,
    ref: Element
  ) => {
    // Check if an undo record for this identifier already exists.
    const existing = this.changeStack.get(identifier);

    // If no record exists, this is the first time we're applying this transformation.
    if (!existing) {
      // Mark the new element with a data attribute for tracking.
      ref.setAttribute(TRANSFORMATION_DATA_ATTR_KEY, identifier);
      // Create a new entry in the change stack with the necessary undo information.
      this.changeStack.set(identifier, {
        undoOpts: {
          type: "Replace",
          ref, // The new element to be replaced back on undo.
          content: originalOuterHtml, // The original content to restore.
        },
      });
      return;
    }

    // If a record already exists, we are updating the reference.
    // This happens when a transition completes and the final element is different
    // from the one we started with (e.g., the wrapper).
    const previousRef = existing.undoOpts.ref;
    if (previousRef && previousRef !== ref && previousRef.isConnected) {
      // Clean up the data attribute from the old element reference if it's still in the DOM.
      previousRef.removeAttribute(TRANSFORMATION_DATA_ATTR_KEY);
    }

    // Mark the new element reference and update the change stack entry.
    ref.setAttribute(TRANSFORMATION_DATA_ATTR_KEY, identifier);
    existing.undoOpts.ref = ref;
  };

  private syncChangeStack = () => {
    let pruned = 0;

    for (const [id, entry] of this.changeStack) {
      const ref = entry.undoOpts.ref;
      const alive = !!ref && (ref.isConnected || document.contains(ref));

      // drop if the node is gone
      if (!alive) {
        this.changeStack.delete(id);
        pruned++;
        continue;
      }

      // drop if our marker no longer matches (not our transform anymore)
      const marker = ref.getAttribute(TRANSFORMATION_DATA_ATTR_KEY);
      if (marker !== id) {
        this.changeStack.delete(id);
        pruned++;
      }
    }

    this.log(
      "debug",
      `syncChangeStack pruned ${this.formatThousands(pruned)} stale entries`
    );
  };

  public undo = (identifier: string) => {
    const stackItem = this.changeStack.get(identifier);

    if (stackItem === undefined) {
      throw new Error(
        `Cannot undo stack item with identifier ${identifier} as it does not exist.`
      );
    }

    const { undoOpts } = stackItem;
    switch (undoOpts.type) {
      case "Remove": {
        undoOpts.ref.remove();
        break;
      }
      case "Replace": {
        morphdom(undoOpts.ref, undoOpts.content);
        undoOpts.ref.removeAttribute(TRANSFORMATION_DATA_ATTR_KEY);
        break;
      }
      case "SetInput": {
        if (!this.isInputElement(undoOpts.ref))
          throw new Error("Element targeted is not of a supported type");
        this.setInputValue(undoOpts.ref, undoOpts.content);
        undoOpts.ref.removeAttribute(TRANSFORMATION_DATA_ATTR_KEY);
        break;
      }
    }

    this.changeStack.delete(identifier);
  };

  public undoAll = () => {
    this.changeStack.forEach((_, ident) => this.undo(ident));
  };

  /**
   * Toggles between personalized and original content.
   * Updates CueCard state accordingly.
   */
  public toggleTransformations = async () => {
    if (this.isPersonalized) {
      // Currently personalized, switch to original
      this.undoAll();
      this.isPersonalized = false;
      this.log("debug", "Toggled to original content");
    } else {
      // Currently showing original, re-apply transformations
      if (this.lastAppliedTransformations.length > 0) {
        await this.applyTransformations(this.lastAppliedTransformations, {
          shouldConsolidate: true,
        });
      }
      this.isPersonalized = true;
      this.log("debug", "Toggled to personalized content");
    }
    this.updateCueCardPersonalizationState();
  };

  /**
   * Updates the CueCard's isPersonalized state to sync the toggle button.
   */
  private updateCueCardPersonalizationState = () => {
    if (this.cueCardInstance) {
      this.cueCardInstance.update({ isPersonalized: this.isPersonalized });
    }
  };

  private setInputValue = (
    el: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement,
    value: string
  ) => {
    const proto =
      el instanceof HTMLTextAreaElement
        ? HTMLTextAreaElement.prototype
        : el instanceof HTMLInputElement
        ? HTMLInputElement.prototype
        : HTMLSelectElement.prototype;

    const descriptor = Object.getOwnPropertyDescriptor(proto, "value");
    if (!descriptor?.set) {
      throw new Error("No value setter found");
    }
    descriptor.set.call(el, value); // native setter updates the DOM property without clobbering React
    el.dispatchEvent(new Event("input", { bubbles: true })); // React listens for this
    el.dispatchEvent(new Event("change", { bubbles: true })); // some code listens to change
  };

  /*
  ----
  --   CUE CARD AUTO-MOUNT & PERSONALIZATION
  ----
  */

  /**
   * Fetches the CueCard configuration from the API to check if an active
   * template exists for the given path.
   *
   * @param pagePath - The page path to check for active template
   * @returns The config response, or null if the request failed
   */
  private fetchCueCardConfig = async (
    pagePath: string
  ): Promise<CueCardConfigResponse | null> => {
    if (!this.config.publicKey) return null;

    try {
      const searchParams = new URLSearchParams({
        publicKey: this.config.publicKey,
        pagePath,
      });

      const response = await fetch(
        `${this.config.apiHost}/v1/cue-card-config?${searchParams.toString()}`
      );

      if (!response.ok) {
        this.log(
          "warn",
          `CueCard config request failed with status ${response.status}`
        );
        return null;
      }

      const data = await response.json();

      if (!isCueCardConfigResponse(data)) {
        this.log("warn", "Invalid CueCard config response format");
        return null;
      }

      return data;
    } catch (error) {
      this.log("error", "Failed to fetch CueCard config:", error);
      return null;
    }
  };

  /**
   * Initializes and mounts a CueCard with configuration from the API.
   * Called automatically when publicKey is provided.
   *
   * The CueCard will only be mounted if an active template exists for
   * the current path on the server.
   */
  private initCueCard = async () => {
    if (this.cueCardInstance) {
      this.log("warn", "CueCard already initialized, skipping...");
      return;
    }

    // Fetch config from API to check if active template exists for this path
    const configResponse = await this.fetchCueCardConfig(this.currentPath);

    if (!configResponse || !configResponse.enabled) {
      this.log(
        "debug",
        `CueCard not mounted: no active template for path "${this.currentPath}"`
      );
      return;
    }

    this.log("debug", "Active template found, mounting CueCard...");

    // Use server config if available, otherwise fall back to defaults
    const serverConfig = configResponse.config;
    const deanonymizationConfig = serverConfig?.deanonymization;
    const shouldWaitForDeanonymization = !!deanonymizationConfig?.provider;

    this.cueCardInstance = new CueCard({
      theme: serverConfig?.theme ?? "light",
      position: serverConfig?.position ?? "top-center",
      direction: serverConfig?.direction ?? "top-to-bottom",
      fields: serverConfig?.fields ?? [
        {
          name: "companyDomain",
          label: "Company Name or Domain",
          placeholder: "example.com",
          required: true,
          minLength: 3,
          errorMessage: "Company name or domain is required",
        },
      ],
      isVisible: !shouldWaitForDeanonymization,
      enableFocusMode: serverConfig?.enableFocusMode ?? false,
      showWatermark: serverConfig?.showWatermark ?? false,
      showKeyboardHints: serverConfig?.showKeyboardHints ?? true,
      enableLauncher: serverConfig?.enableLauncher ?? true,
      customTheme: serverConfig?.customTheme,
      textOverrides: serverConfig?.textOverrides,
      enableUndoToggle: serverConfig?.enableUndoToggle ?? false,
      deanonymization: deanonymizationConfig,
      isPersonalized: this.isPersonalized,
      onDismiss: () => {
        this.log("debug", "CueCard dismissed by user");
        this.dispatchKenobiEvent("cuecard:dismiss", {
          pagePath: this.currentPath,
        });
      },
      onSubmitPersonalization: (values) => {
        this.log("debug", "CueCard submitted:", values);
        void this.personalize(values);
      },
      onOpenChange: (isOpen) => {
        this.log("debug", "CueCard open state changed:", isOpen);
        this.dispatchKenobiEvent("cuecard:openchange", {
          isOpen,
          pagePath: this.currentPath,
        });
      },
      onTogglePersonalization: () => {
        this.log("debug", "CueCard toggle personalization");
        void this.toggleTransformations();
        this.dispatchKenobiEvent("cuecard:toggle", {
          isPersonalized: this.isPersonalized,
          pagePath: this.currentPath,
        });
      },
    });

    this.cueCardInstance.mount();
    if (shouldWaitForDeanonymization) {
      this.setupDeanonymizationPrefetch(
        deanonymizationConfig,
        serverConfig?.fields
      );
    }
    this.log("debug", "CueCard mounted successfully");
  };

  private isSameInput = (
    a: Record<string, string>,
    b: Record<string, string>
  ): boolean => {
    const aKeys = Object.keys(a).sort();
    const bKeys = Object.keys(b).sort();
    if (aKeys.length !== bKeys.length) return false;
    for (let i = 0; i < aKeys.length; i += 1) {
      const key = aKeys[i];
      if (key !== bKeys[i]) return false;
      if ((a[key] ?? "") !== (b[key] ?? "")) return false;
    }
    return true;
  };

  private buildInputFromFields = (
    fields: Array<{ name: string }>,
    companyName: string,
    companyDomain: string
  ): Record<string, string> | null => {
    const fallback = companyDomain || companyName;
    if (!fallback) return null;
    const input: Record<string, string> = {};
    fields.forEach((field) => {
      const key = field.name;
      const lowerKey = key.toLowerCase();
      if (lowerKey.includes("domain")) {
        input[key] = companyDomain || fallback;
        return;
      }
      if (lowerKey.includes("company")) {
        input[key] = companyName || fallback;
        return;
      }
      input[key] = fallback;
    });
    return input;
  };

  private buildDeanonymizedInput = (
    detail: IdentityResolutionDetail,
    fields?: Array<{ name: string }>
  ): Record<string, string> | null => {
    const name = detail.company?.name || detail.domain || "";
    const domain = detail.company?.domain || detail.domain || "";
    if (!fields || fields.length === 0) {
      if (!name && !domain) return null;
      return {
        companyName: name || domain,
        companyDomain: domain || name,
      };
    }
    return this.buildInputFromFields(fields, name, domain);
  };

  private requestPersonalization = async (
    input: Record<string, string>,
    options?: PersonalizationRequestOptions
  ): Promise<PersonalizationApiResult> => {
    const payload: Record<string, unknown> = {
      publicKey: this.config.publicKey,
      input,
      pagePath: this.currentPath,
    };

    if (options?.notifySlack === false) {
      payload.notifySlack = false;
    }
    if (options?.notifyOnly) {
      payload.notifyOnly = true;
    }
    if (options?.predictionSource) {
      payload.predictionSource = options.predictionSource;
    }
    if (options?.prefetchedTransformations) {
      payload.prefetchedTransformations = options.prefetchedTransformations;
    }
    if (options?.prefetchedMetadata) {
      payload.prefetchedMetadata = options.prefetchedMetadata;
    }

    const response = await fetch(`${this.config.apiHost}/v1/personalize`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throw new Error(errorBody.error || `API error: ${response.status}`);
    }

    const result = (await response.json()) as PersonalizationApiResult;
    this.log("debug", "Personalization API response:", result);
    return result;
  };

  public preparePersonalization = async (
    input: Record<string, string>,
    options?: { source?: PersonalizationPredictionSource }
  ): Promise<PersonalizationApiResult | null> => {
    if (!this.config.publicKey) {
      this.log(
        "error",
        "Cannot prepare personalization: publicKey not configured"
      );
      return null;
    }

    if (
      this.preparedPersonalization?.input &&
      this.isSameInput(this.preparedPersonalization.input, input)
    ) {
      if (options?.source && !this.preparedPersonalization.source) {
        this.preparedPersonalization = {
          ...this.preparedPersonalization,
          source: options.source,
        };
      }
      return this.preparedPersonalization.result;
    }

    if (this.preparePersonalizationPromise) {
      return this.preparePersonalizationPromise;
    }
    this.isPreparingPersonalization = true;

    this.preparePersonalizationPromise = (async () => {
      try {
        const result = await this.requestPersonalization(input, {
          notifySlack: false,
          predictionSource: options?.source,
        });
        this.preparedPersonalization = {
          input,
          result,
          source: options?.source,
        };
        this.dispatchKenobiEvent("personalization:ready", {
          input,
          pagePath: this.currentPath,
          transformationsCount: result.transformations?.length ?? 0,
          metadata: result.metadata,
          source: options?.source,
        });
        return result;
      } catch (error) {
        this.log("error", "Personalization prefetch failed:", error);
        return null;
      } finally {
        this.isPreparingPersonalization = false;
        this.preparePersonalizationPromise = null;
      }
    })();

    return this.preparePersonalizationPromise;
  };

  private notifyPreparedPersonalization = async (
    prepared: PreparedPersonalization
  ): Promise<void> => {
    if (!prepared.result.transformations || !prepared.result.metadata) {
      this.log(
        "warn",
        "Prepared personalization missing transformations or metadata for notify"
      );
      return;
    }

    try {
      await this.requestPersonalization(prepared.input, {
        notifySlack: true,
        notifyOnly: true,
        predictionSource: prepared.source,
        prefetchedTransformations: prepared.result.transformations,
        prefetchedMetadata: prepared.result.metadata,
      });
    } catch (error) {
      this.log(
        "warn",
        "Failed to send Slack notification for prepared personalization:",
        error
      );
    }
  };

  private setupDeanonymizationPrefetch = (
    config?: { timeoutMs?: number },
    fields?: Array<{ name: string }>
  ) => {
    if (!this.cueCardInstance) return;
    if (typeof window === "undefined") {
      this.cueCardInstance.update({ isVisible: true });
      return;
    }

    if (this.deanonymizationPrefetchCleanup) return;

    let resolved = false;
    let hasIdentity = false;
    let shouldDisableDeanonymization = false;
    const finish = () => {
      if (resolved) return;
      resolved = true;
      if (shouldDisableDeanonymization) {
        this.cueCardInstance?.update({
          deanonymization: undefined,
          isVisible: true,
        });
      } else {
        this.cueCardInstance?.update({ isVisible: true });
      }
      this.deanonymizationPrefetchCleanup?.();
      this.deanonymizationPrefetchCleanup = null;
    };

    const handleIdentity = async (
      event: Event | { detail?: IdentityResolutionDetail }
    ) => {
      if (resolved) return;
      const detail = (event as CustomEvent<IdentityResolutionDetail>).detail;
      hasIdentity = true;
      if (detail?.type !== "business") {
        finish();
        return;
      }

      const input = this.buildDeanonymizedInput(detail, fields);
      if (!input) {
        finish();
        return;
      }

      const prepared = await this.preparePersonalization(input, {
        source: "deanonymized",
      });
      if (!prepared) {
        shouldDisableDeanonymization = true;
      }

      finish();
    };

    const cachedIdentity = window.__kenobiIdentityResolution as
      | IdentityResolutionDetail
      | null
      | undefined;
    if (cachedIdentity) {
      void handleIdentity({ detail: cachedIdentity });
    }

    window.addEventListener(IDENTITY_RESOLVED_EVENT, handleIdentity);
    const timeoutMs = config?.timeoutMs ?? DEFAULT_DEANON_TIMEOUT_MS;
    const timeoutId = window.setTimeout(() => {
      if (!hasIdentity) {
        finish();
      }
    }, timeoutMs + 50);

    this.deanonymizationPrefetchCleanup = () => {
      window.removeEventListener(IDENTITY_RESOLVED_EVENT, handleIdentity);
      window.clearTimeout(timeoutId);
    };
  };

  /**
   * Syncs CueCard visibility based on the current path.
   * Checks with the server if an active template exists for the new path.
   * Called after navigation changes.
   */
  private syncCueCardVisibility = async () => {
    // Only manage CueCard if publicKey is configured
    if (!this.config.publicKey) return;

    // Unmount existing CueCard on navigation (will re-mount if new path has active template)
    if (this.cueCardInstance) {
      this.log("debug", `Navigation detected, unmounting CueCard`);
      this.cueCardInstance.unmount();
      this.cueCardInstance = null;
    }

    // Check if new path has an active template
    void this.initCueCard();
  };

  /**
   * Dispatches a custom event on window for external listeners.
   * Events are prefixed with "kenobi:" for namespacing.
   */
  private dispatchKenobiEvent = (
    eventName: string,
    detail: Record<string, unknown>
  ) => {
    const event = new CustomEvent(`kenobi:${eventName}`, {
      detail,
      bubbles: true,
    });
    window.dispatchEvent(event);
    this.log("debug", `Dispatched event: kenobi:${eventName}`, detail);
  };

  /**
   * Triggers personalization by calling the API with the provided input.
   * Updates CueCard status during the flow and applies transformations.
   *
   * Dispatches events:
   * - `kenobi:personalization:start` - when personalization begins
   * - `kenobi:personalization:complete` - when personalization succeeds
   * - `kenobi:personalization:error` - when personalization fails
   *
   * @param input - User-provided fields (e.g., { companyDomain: "acme.com" })
   */
  public personalize = async (
    input: Record<string, string>
  ): Promise<PersonalizeApiResponse | null> => {
    if (!this.config.publicKey) {
      this.log("error", "Cannot personalize: publicKey not configured");
      return null;
    }

    const startTime = new Date();
    this.log("debug", "Starting personalization with input:", input);

    const prepared =
      this.preparedPersonalization?.input &&
      this.isSameInput(this.preparedPersonalization.input, input)
        ? this.preparedPersonalization
        : null;
    const isDeanonymizedInput = prepared?.source === "deanonymized";

    // Dispatch start event
    this.dispatchKenobiEvent("personalization:start", {
      input,
      pagePath: this.currentPath,
      deanonymized: isDeanonymizedInput,
    });

    try {
      // Update CueCard to loading state
      if (this.cueCardInstance) {
        this.cueCardInstance.update({
          status: "starting",
          progressPct: prepared ? 80 : 10,
        });
      }

      if (prepared) {
        void this.notifyPreparedPersonalization(prepared);
      }

      const result = prepared
        ? prepared.result
        : await this.requestPersonalization(input);

      // Apply transformations
      if (result.transformations && Array.isArray(result.transformations)) {
        await this.applyTransformations(result.transformations, {
          shouldConsolidate: true,
        });
      }

      if (prepared) {
        this.preparedPersonalization = null;
      }

      // Update CueCard to success state
      if (this.cueCardInstance) {
        this.cueCardInstance.update({ status: "success", progressPct: 100 });
      }

      const endTime = new Date();
      const duration = this.getDurationInMilliseconds(startTime, endTime);
      this.log(
        "debug",
        `Personalization complete, took ${this.formatThousands(duration)} ms`
      );

      // Dispatch complete event
      this.dispatchKenobiEvent("personalization:complete", {
        input,
        pagePath: this.currentPath,
        durationMs: duration,
        transformationsCount: result.transformations?.length ?? 0,
        metadata: result.metadata,
        source: prepared ? "prefetch" : "api",
        deanonymized: isDeanonymizedInput,
      });

      return result;
    } catch (error) {
      this.log("error", "Personalization failed:", error);

      // Update CueCard to error state
      if (this.cueCardInstance) {
        this.cueCardInstance.update({ status: "error", progressPct: 0 });
      }

      // Dispatch error event
      this.dispatchKenobiEvent("personalization:error", {
        input,
        pagePath: this.currentPath,
        error: error instanceof Error ? error.message : String(error),
        deanonymized: isDeanonymizedInput,
      });
    }

    return null;
  };

  /**
   * Returns the CueCard instance if one was auto-mounted.
   * Useful for external control of the CueCard state.
   */
  public getCueCard = (): CueCard | null => {
    return this.cueCardInstance;
  };
}

export { CueCard, Kenobi };
